/** Photo or graphic on the developer careers page. Path under `apps/app/public/…`. */
export interface FunnelImageSlot {
  readonly src: string;
  readonly alt: string;
  /** Native image title attribute — used for SEO on public careers photos. */
  readonly title?: string;
  readonly hint: string;
  readonly ratio: string;
  readonly objectFit?: "cover" | "contain";
  /** Rounded label under the photo. Omit when the image needs no caption. */
  readonly caption?: string;
}

/** Same-origin HTML embed (architecture diagram) instead of a photo. */
export interface FunnelEmbedSlot {
  readonly src: string;
  readonly title: string;
  /** Small link under the embed. Requires `captionHref`. */
  readonly caption?: string;
  /** Opens in a new tab. Requires `caption`. */
  readonly captionHref?: string;
}

export interface FunnelBullet {
  readonly icon: FunnelIconKey;
  readonly title: string;
  readonly text: string;
}

/** Turns one word in `intro` into a YouTube link (engineering stack only). */
export interface FunnelIntroYoutube {
  readonly word: string;
  readonly href: string;
}

/**
 * One band on the page. Always the same layout: image, heading, bullets.
 */
export interface FunnelSection {
  readonly id: string;
  readonly heading: string;
  readonly intro?: string;
  readonly introYoutube?: FunnelIntroYoutube;
  readonly image?: FunnelImageSlot;
  readonly embed?: FunnelEmbedSlot;
  /** Device composition from the main careers page (laptop + phone + badges). */
  readonly media?: "product-mockup" | "growth-chart";
  readonly bullets: readonly FunnelBullet[];
}

export interface FunnelHero {
  readonly topBar: string;
  readonly heading: string;
  readonly facts: readonly string[];
}

export interface FunnelFooter {
  readonly line: string;
  readonly youtube: string;
  readonly imprint: string;
  readonly privacy: string;
}

/** Image sections on the developer careers page. */
export interface FunnelContent {
  readonly hero: FunnelHero;
  readonly sections: readonly FunnelSection[];
  readonly footer: FunnelFooter;
}

export type FunnelIconKey =
  | "inbox"
  | "agent"
  | "plug"
  | "globe"
  | "shield"
  | "clock"
  | "coins"
  | "rocket"
  | "users"
  | "code"
  | "database"
  | "sparkles"
  | "trending-up";

export const SRC = {
  office: "/marketing/careers/ki-plattform-hausverwaltung-worms.webp",
  mapEn: "/marketing/careers/remote-team-map-europe.webp",
  architecture: "/marketing/careers/architektur-diagramm.html?v=5",
} as const;

export const YOUTUBE_HREF = "https://www.youtube.com/@hausverwalter-ai";
export const LINKEDIN_HREF = "https://de.linkedin.com/company/hausverwalter-ai";

/**
 * Portrait used on the Ja! CTA and the application form.
 *
 * Square 600×600 crop. Face sits near the top — always use object-position
 * near the top, never `center`, or the crop cuts the face off.
 */
export const PORTRAIT = {
  src: "/marketing/careers/jonathan-tausendfreund.jpg",
  alt: "Jonathan Tausendfreund, CEO von hausverwalter.ai",
  name: "Jonathan Tausendfreund",
  role: "CEO",
  objectPosition: "50% 0%",
} as const;

export const ROLE_LABELS = {
  frontend: "Frontend Developer (application page)",
  backend: "Backend Developer (application page)",
  fullstack: "Fullstack Developer (application page)",
  unknown: "Developer (application page)",
} as const;
